import {
  AnnouncementBannerFlagCard,
  type AnnouncementBannerValues,
} from "@/components/admin/announcement-banner-flag-card";
import { PageHeader } from "@/components/layout/page-header";
import { featureFlagsKeys } from "@/lib/queries/feature-flags";
import { api as trpc, trpcClient } from "@/lib/trpc";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Label,
  Skeleton,
  Switch,
} from "@repo/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { toast } from "sonner";

export const Route = createFileRoute("/admin/feature-flags")({
  component: FeatureFlagsPage,
});

function FeatureFlagsPage() {
  const queryClient = useQueryClient();

  const { data: flags } = useQuery(trpc.admin.getFeatureFlags.queryOptions());

  const { mutate: toggleFlag } = useMutation({
    mutationFn: (
      vars: Parameters<typeof trpcClient.admin.toggleFeatureFlag.mutate>[0],
    ) => trpcClient.admin.toggleFeatureFlag.mutate(vars),
    onMutate: async ({ key, enabled }) => {
      await queryClient.cancelQueries({
        queryKey: trpc.admin.getFeatureFlags.queryKey(),
      });

      const previousFlags = queryClient.getQueryData(
        trpc.admin.getFeatureFlags.queryKey(),
      );

      queryClient.setQueryData(trpc.admin.getFeatureFlags.queryKey(), (old) => {
        if (!old) return old;
        return old.map((flag) =>
          flag.key === key ? { ...flag, enabled } : flag,
        );
      });

      return { previousFlags };
    },
    onError: (err, _, context) => {
      toast.error(err.message);
      if (context?.previousFlags) {
        queryClient.setQueryData(
          trpc.admin.getFeatureFlags.queryKey(),
          context.previousFlags,
        );
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: trpc.admin.getFeatureFlags.queryKey(),
      });
      toast.success("Feature flag updated");
    },
  });

  const { mutateAsync: saveBanner } = useMutation({
    mutationFn: (
      vars: AnnouncementBannerValues & { expectedUpdatedAt: Date | null },
    ) => trpcClient.admin.updateAnnouncementBanner.mutate(vars),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: trpc.admin.getFeatureFlags.queryKey(),
        }),
        queryClient.invalidateQueries({
          queryKey: featureFlagsKeys.announcementBanner(),
        }),
      ]);
      toast.success("Announcement banner saved");
    },
    onError: (err) => {
      toast.error(err.message);
    },
  });

  const handleSaveBanner = async (
    values: AnnouncementBannerValues,
    expectedUpdatedAt: Date | null,
  ) => {
    try {
      const saved = await saveBanner({ ...values, expectedUpdatedAt });
      return saved.updatedAt;
    } catch {
      // onError already showed the toast; the form keeps the admin's edits.
      return null;
    }
  };

  const notificationsFlag = flags?.find((f) => f.key === "notifications_v2");
  const bannerFlag = flags?.find((f) => f.key === "showAnnouncementBanner");

  return (
    <div className="flex flex-col gap-6 p-6 h-full">
      <PageHeader
        title="Feature Flags"
        description="Manage system-wide feature flags"
      />

      <Card>
        <CardHeader>
          <CardTitle>Notifications</CardTitle>
          <CardDescription>
            Control the rollout of notification features.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between space-x-2">
            <Label
              htmlFor="notifications-v2"
              className="flex flex-col space-y-1"
            >
              <span>Use New Notifications Page</span>
              <span className="font-normal text-xs text-muted-foreground">
                Enable the redesigned notifications interface for all users.
              </span>
            </Label>
            <Switch
              id="notifications-v2"
              checked={notificationsFlag?.enabled ?? false}
              onCheckedChange={(checked) =>
                toggleFlag({ key: "notifications_v2", enabled: checked })
              }
            />
          </div>
        </CardContent>
      </Card>

      {bannerFlag ? (
        <AnnouncementBannerFlagCard
          savedAt={bannerFlag.updatedAt}
          initialValues={{
            enabled: bannerFlag.enabled,
            message: bannerFlag.settings?.message ?? "",
            linkUrl: bannerFlag.settings?.linkUrl ?? null,
          }}
          onSave={handleSaveBanner}
        />
      ) : (
        <Skeleton
          className="h-80 w-full rounded-xl"
          aria-label="Loading announcement banner"
        />
      )}
    </div>
  );
}
